import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import AddPartnerSourceModal from "components/Companies/AddPartnerSourceModal";
import ChangeRoleModal from "components/Companies/ChangeRoleModal";
import { Users, Plus, Pencil, Trash2, Building2, AlertTriangle } from "lucide-react";

/**
 * SOUND-294 — Internal-only Sources management section on the Company Detail
 * Overview tab. Lets internal users add a partner, change a partner's role,
 * or remove a partner from a company's sources[]. When removing the last
 * partner, prompts for a replacement sourceType ("self-serve" or "internal")
 * as enforced by the backend invariant.
 */
const ROLE_COLORS = {
  primary: "badge-success",
  equal: "badge-info",
  secondary: "badge",
};

function RolePill({ role }) {
  const cls = ROLE_COLORS[role] || "badge";
  return (
    <span className={`badge ${cls} capitalize`}>{role || "unknown"}</span>
  );
}

function SourceTypePill({ sourceType }) {
  if (!sourceType) return <span className="badge">unknown</span>;
  const label = sourceType.replace("-", " ");
  return <span className="badge capitalize">{label}</span>;
}

export default function SourcesCard({ company, refetch }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [partnerNames, setPartnerNames] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [changeRoleFor, setChangeRoleFor] = useState(null); // source entry
  const [confirmRemove, setConfirmRemove] = useState(null); // source entry
  const [removeFallbackType, setRemoveFallbackType] = useState("self-serve");
  const [removing, setRemoving] = useState(false);

  const sources = Array.isArray(company?.sources) ? company.sources : [];

  // Reset the fallback type whenever we open a fresh remove dialog
  useEffect(() => {
    if (confirmRemove) setRemoveFallbackType("self-serve");
  }, [confirmRemove]);

  // Partner names: the API's enrichWithSource attaches `source.partners[]`
  // with { partnerId, partnerName, role }. Use that when present; fall back
  // to individual fetches for any missing names (defensive).
  useEffect(() => {
    const nameMap = {};
    if (Array.isArray(company?.source?.partners)) {
      for (const p of company.source.partners) {
        if (p?.partnerId && p.partnerName) nameMap[p.partnerId] = p.partnerName;
      }
    }
    setPartnerNames(nameMap);
  }, [company]);

  const fetchMissingName = useCallback(
    async (partnerId) => {
      if (!partnerId || partnerNames[partnerId]) return;
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getCompany(partnerId),
          auth.user,
          effectiveCompanyId
        );
        if (res?.data?.name) {
          setPartnerNames((prev) => ({ ...prev, [partnerId]: res.data.name }));
        }
      } catch {
        // non-fatal
      }
    },
    [auth?.user, effectiveCompanyId, partnerNames]
  );

  useEffect(() => {
    // Fire off lookups for any partnerId whose name didn't come through the
    // batched resolver (shouldn't happen in practice, but keeps the UI sane
    // if the partner doc was deleted).
    for (const s of sources) {
      if (s?.partnerId && !partnerNames[s.partnerId]) {
        fetchMissingName(s.partnerId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources.length]);

  const willEmptyOnRemove = sources.length === 1;

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      const body = willEmptyOnRemove ? { sourceType: removeFallbackType } : undefined;
      await makeApiRequest(
        "delete",
        apiRoutes.removeCompanySource(company.companyId, confirmRemove.partnerId),
        auth.user,
        effectiveCompanyId,
        body
      );
      toast({
        status: "success",
        title: "Partner removed",
        description: willEmptyOnRemove
          ? `${company.name} is now ${removeFallbackType}.`
          : `Partner removed from ${company.name}.`,
      });
      setConfirmRemove(null);
      refetch();
    } catch (err) {
      console.error("[SourcesCard] Remove failed", err);
      toast({
        status: "destructive",
        title: "Failed to remove partner",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="surface">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand/20 rounded-lg">
            <Users className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Sources</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Who sourced this company. Edit attribution after the fact.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SourceTypePill sourceType={company.sourceType} />
        </div>
      </div>

      <div className="p-6 space-y-3">
        {sources.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">
            No partner sources.{" "}
            {company.sourceType === "self-serve"
              ? "This company signed up directly."
              : company.sourceType === "internal"
              ? "Sound Commerce onboarded this company directly."
              : ""}
          </div>
        ) : (
          <ul className="space-y-2">
            {sources.map((s) => {
              const name = partnerNames[s.partnerId] || s.partnerId;
              return (
                <li
                  key={s.partnerId}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/40"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-md bg-brand/20 shrink-0">
                      <Building2 className="h-4 w-4 text-brand" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate font-mono">
                        {s.partnerId}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <RolePill role={s.role} />
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setChangeRoleFor(s)}
                      title="Change role"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmRemove(s)}
                      title="Remove partner"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <button
          className="btn btn-outline btn-sm"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Add Partner Source
        </button>
      </div>

      {addOpen && (
        <AddPartnerSourceModal
          company={company}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            refetch();
          }}
        />
      )}

      {changeRoleFor && (
        <ChangeRoleModal
          company={company}
          source={changeRoleFor}
          partnerName={partnerNames[changeRoleFor.partnerId] || changeRoleFor.partnerId}
          onClose={() => setChangeRoleFor(null)}
          onSaved={() => {
            setChangeRoleFor(null);
            refetch();
          }}
        />
      )}

      {/* Remove confirmation — if this would empty sources[], prompt for a new sourceType in the same dialog */}
      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => !removing && setConfirmRemove(null)}
        >
          <div
            className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-full bg-destructive/10 shrink-0">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Remove partner source?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {willEmptyOnRemove
                    ? `${partnerNames[confirmRemove.partnerId] || confirmRemove.partnerId} is the only partner on ${company.name}. Removing them will clear the partner attribution — please pick what this company becomes.`
                    : `${partnerNames[confirmRemove.partnerId] || confirmRemove.partnerId} will no longer be a source for ${company.name}.`}
                </p>
              </div>
            </div>

            {willEmptyOnRemove && (
              <div className="mb-4 space-y-2">
                <label className="text-sm font-medium text-foreground block">
                  New source type
                </label>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    {
                      value: "self-serve",
                      label: "Self-serve",
                      description: "Company signed up on their own.",
                    },
                    {
                      value: "internal",
                      label: "Internal",
                      description: "Sound Commerce onboarded this company directly.",
                    },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        removeFallbackType === opt.value
                          ? "border-brand bg-brand/10"
                          : "border-border hover:bg-muted/60"
                      }`}
                    >
                      <input
                        type="radio"
                        name="remove-source-fallback"
                        value={opt.value}
                        checked={removeFallbackType === opt.value}
                        onChange={() => setRemoveFallbackType(opt.value)}
                        className="mt-0.5 h-4 w-4 border-border text-brand focus:ring-brand"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {opt.label}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-ghost"
                onClick={() => !removing && setConfirmRemove(null)}
                disabled={removing}
              >
                Cancel
              </button>
              <button
                className="btn bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleRemove}
                disabled={removing}
              >
                {removing ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
